import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

// TODO: using a super nested private component
import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { useCreateToleranceSetting } from 'modules/attendance/components/TimeTrackingSettings/ToleranceSettings/hooks/useCreateToleranceSettings'
import type { ToleranceSettingFragment } from 'modules/attendance/components/TimeTrackingSettings/ToleranceSettings/hooks/useToleranceSettings'
import type { ToleranceSetting } from 'modules/attendance/components/TimeTrackingSettings/ToleranceSettings/hooks/useToleranceSettings'
import { useUpdateToleranceSetting } from 'modules/attendance/components/TimeTrackingSettings/ToleranceSettings/hooks/useUpdateToleranceSettings'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import ScheduledShifts from './ScheduledShifts'
import WorkedHours from './WorkedHours'
import ToleranceForm from './form'

export interface ToleranceSettingsProps {
  attendanceSetting: AttendanceSetting
  toleranceSetting: ToleranceSetting | undefined
}

const createForm = (
  attendanceEmployeesSettingId: string,
  tolerance: ToleranceSetting | undefined
) => {
  if (tolerance) {
    return new ToleranceForm({ ...tolerance, attendanceEmployeesSettingId })
  } else {
    return new ToleranceForm({ attendanceEmployeesSettingId })
  }
}

const Form = ({ attendanceSetting, toleranceSetting }: ToleranceSettingsProps) => {
  const [form, setForm] = React.useState(
    createForm(attendanceSetting.attributes.id, toleranceSetting)
  )
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const { create } = useCreateToleranceSetting()
  const { update } = useUpdateToleranceSetting()
  const [running, setRunning] = React.useState(false)
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  useEffect(() => {
    setForm(createForm(attendanceSetting.attributes.id, toleranceSetting))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceSetting.attributes.id, attendanceSetting.attributes.tolerance_setting_id])

  const showFlash = (error?: Error) => {
    app.setFlash({
      type: error ? 'wrong' : 'correct',
      message: error ? error.message : i18n.t('common.saved_successfully'),
    })
  }

  const mutate = async () => {
    if (form.isNewRecord) {
      const { data } = await create(form.parsedData())
      const toleranceSetting = data?.attendanceMutations?.createAttendanceToleranceSetting
        ?.toleranceSetting as ToleranceSettingFragment
      return { toleranceSetting }
    } else {
      const { data } = await update(form.get('id').value, form.parsedData())
      const toleranceSetting = data?.attendanceMutations?.updateAttendanceToleranceSetting
        ?.toleranceSetting as ToleranceSettingFragment
      return { toleranceSetting }
    }
  }

  const saveToleranceSetting = async () => {
    setRunning(true)
    await asyncAction(
      async () => {
        try {
          const { toleranceSetting } = await mutate()
          form.setValues({
            id: toleranceSetting?.id,
            ...toleranceSetting,
          })
          showFlash()
        } catch (error) {
          showFlash(error as Error)
        }
      },
      {
        errorFlash: false,
        successFlash: false,
      }
    )

    setOpenEffectiveAtFormModal(false)
    setRunning(false)
    form.cleanAll()
  }

  const onSubmit = async () => {
    saveToleranceSetting()
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const settingId = newSetting.id

    form.setValues({
      id: newSetting.toleranceSettingId != null ? String(newSetting.toleranceSettingId) : '0',
      attendanceEmployeesSettingId: settingId,
    })
    saveToleranceSetting()

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'preferences',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
    form.resetAll()
  }

  const showEffectiveAtModal = () => {
    setOpenEffectiveAtFormModal(true)
  }

  return (
    <>
      <Box aria-label="attendance-tolerance-setting-form" gap="s24">
        <ScheduledShifts
          form={form}
          onSubmit={onSubmit}
          showEffectiveAtModal={showEffectiveAtModal}
        />
        <WorkedHours form={form} onSubmit={onSubmit} showEffectiveAtModal={showEffectiveAtModal} />
      </Box>
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onSubmit}
          onClose={onClose}
          running={running}
          attendanceSettingId={attendanceSetting.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default observer(Form)
